import {ThreadItem} from './api/types';

/**
 * Turns a conversation into a markdown document the user can keep.
 *
 * The assistant's own replies are already markdown — including the links its tools returned —
 * so they are emitted verbatim and stay clickable in whatever the file is opened in. Tool calls
 * are recorded as one quiet line each so the transcript shows what the assistant actually did,
 * without the arguments and raw output that would bury the conversation.
 */

// Model reasoning is deliberately left out: it is working-out rather than conversation, it can be
// far longer than the answer, and it is not what someone keeping a transcript is after.
const describeToolCall = (toolName: string, error?: string): string =>
    error
        ? `*Used \`${toolName}\` (failed: ${error})*`
        : `*Used \`${toolName}\`*`;

const formatTimestamp = (iso: string): string => {
    const date = new Date(iso);

    return Number.isNaN(date.getTime()) ? iso : date.toLocaleString();
};

export interface ThreadMarkdownOptions {
    assistantName: string;
    items: ThreadItem[];
    /** Shown under the title so a saved transcript can be placed in time. */
    exportedAt?: Date;
}

export function threadToMarkdown({
    assistantName,
    items,
    exportedAt = new Date()
}: ThreadMarkdownOptions): string {
    const lines: string[] = [`# ${assistantName}`, ''];

    const firstItem = items.find(item => item.createdAt);
    if (firstItem) {
        lines.push(`Conversation started ${formatTimestamp(firstItem.createdAt)}.`);
    }

    lines.push(`Exported ${exportedAt.toLocaleString()}.`, '');

    for (const item of items) {
        switch (item.kind) {
            case 'userMessage': {
                lines.push('## You', '');

                if (item.text?.trim()) {
                    lines.push(item.text.trim(), '');
                }

                if (item.attachments?.length) {
                    lines.push(
                        ...item.attachments.map(a => `- Attached: ${a.fileName}`),
                        ''
                    );
                }

                break;
            }

            case 'assistantMessage': {
                if (!item.text?.trim()) {
                    break;
                }

                // Verbatim: it is already markdown, links and all.
                lines.push(`## ${assistantName}`, '', item.text.trim(), '');
                break;
            }

            case 'toolCall': {
                lines.push(describeToolCall(item.toolName, item.error), '');
                break;
            }

            case 'approvalRequest': {
                lines.push(
                    `*Asked for approval to run \`${item.toolName}\`${
                        item.description ? `: ${item.description}` : ''
                    }*`,
                    ''
                );
                break;
            }

            case 'approvalResponse': {
                lines.push(`*Approval ${item.approved ? 'granted' : 'declined'}*`, '');
                break;
            }

            case 'pageChange': {
                if (item.description) {
                    lines.push(`*Moved to ${item.description}*`, '');
                }
                break;
            }

            case 'reasoning':
            default:
                break;
        }
    }

    return lines.join('\n').replace(/\n{3,}/g, '\n\n').trimEnd() + '\n';
}

/**
 * Hands the transcript to the browser as a download.
 *
 * Self-contained on purpose: the assistant module is shared verbatim between products, so it
 * cannot depend on a helper that only one of them happens to have.
 */
export function saveMarkdown(markdown: string, fileName: string): void {
    const url = URL.createObjectURL(
        new Blob([markdown], {type: 'text/markdown;charset=utf-8'})
    );

    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = fileName;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
}

/** A filesystem-safe, dated name, e.g. "documentation-guide-2026-07-25-1432.md". */
export function threadMarkdownFileName(assistantName: string, exportedAt = new Date()): string {
    const slug = assistantName
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '') || 'assistant';

    const pad = (value: number) => value.toString().padStart(2, '0');

    const stamp =
        `${exportedAt.getFullYear()}-${pad(exportedAt.getMonth() + 1)}-${pad(exportedAt.getDate())}` +
        `-${pad(exportedAt.getHours())}${pad(exportedAt.getMinutes())}`;

    return `${slug}-${stamp}.md`;
}
